import { useQueryClient } from "@tanstack/react-query";
import { router, useRootNavigationState } from "expo-router";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { ActivityIndicator, Platform, View } from "react-native";

import LeaderboardSeasonGate from "@/src/components/leaderboard/leaderboard-season-gate";
import { BlocksUser } from "@/src/models/user.model";
import { accountStore, StoredAccount } from "@/src/services/api/account-store";
import { authEvents } from "@/src/services/api/auth-events";
import {
  isAuthenticated as checkAuthenticated,
  clearTokens,
  decodeToken,
  getOrgSelected,
  getRefreshToken,
  getToken,
  invalidateIdentity,
  refreshTokens,
  saveRefreshToken,
  saveToken,
  setOrgSelected,
  webServerLogout,
} from "@/src/services/api/auth.api";
import { useBlocksUserByIdQuery } from "@/src/services/users.blocks.api";
import { useNotificationConnection } from "../hooks/features/notification/use-notifications";
import { useGetUserGroupIdsQuery } from "../services/profile/profile.hooks";
import { useOrganizationsQuery } from "../services/uam/permissions.blocks.api";
import { getPrimaryRole } from "../utils/role.utils";

type JwtPayload = Readonly<{
  user_id: string;
  roles?: string | string[];
  email: string;
  org_id?: string;
}>;

const toRoleArray = (roles?: string | string[] | null): string[] => {
  if (Array.isArray(roles))
    return roles.filter((r): r is string => typeof r === "string");
  if (typeof roles === "string")
    return roles
      .split(",")
      .map((r) => r.trim())
      .filter(Boolean);
  return [];
};

type AuthContextType = Readonly<{
  user: BlocksUser | null;
  orgId: string | undefined;
  /** Every role the user holds for the active org (union of JWT claim + membership). */
  roles: string[];
  userGroups: string[] | null;
  isAdmin: boolean;
  isAuthenticated: boolean;
  isLoading: boolean;
  isSwitching: boolean;
  appRole: string | null;
  refreshAuth: (skipRedirect?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  isSuperAdmin: boolean;
  accounts: StoredAccount[];
  switchAccount: (userId: string) => Promise<void>;
  addAccount: () => void;
  currentOrgName: string | undefined;
}>;

const AuthContext = createContext<AuthContextType | null>(null);

const deriveRole = (
  user: BlocksUser | null,
  jwtPayload: JwtPayload | null,
  orgId: string | undefined,
  storedRole?: string,
): string | null => {
  const jwtRoles = toRoleArray(jwtPayload?.roles);
  if (jwtRoles.length) return jwtRoles[0];

  const currentOrgMembership = user?.memberships?.find(
    (membership) => membership.organizationId === orgId,
  );
  const membershipRole = getPrimaryRole(currentOrgMembership?.roles);
  if (membershipRole) return membershipRole;

  return storedRole ?? null;
};

// The "default" org is a backend bookkeeping tenant the user never actually
// belongs to — sessions must never operate under it. Everything org-related is
// derived from the user's *real* (non-default) memberships only.
const DEFAULT_ORG_ID = "default";

const getRealMemberships = (user: BlocksUser) =>
  user.memberships?.filter(
    (membership) => membership.organizationId !== DEFAULT_ORG_ID,
  ) ?? [];

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const queryClient = useQueryClient();
  const [initialized, setInitialized] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [jwtPayload, setJwtPayload] = useState<JwtPayload | null>(null);
  const [storedRole, setStoredRole] = useState<string | undefined>(undefined);
  const [accounts, setAccounts] = useState<StoredAccount[]>([]);
  const [isSwitching, setIsSwitching] = useState(false);
  const [orgSelected, setOrgSelectedState] = useState(false);
  const hasRedirected = useRef(false);
  const wasAuthenticated = useRef(false);
  const rootNavReady = !!useRootNavigationState()?.key;

  useEffect(() => {
    const initAuth = async () => {
      try {
        // Web: the token is an httpOnly cookie. On reload the in-memory access
        if (Platform.OS === "web" && !(await getToken())) {
          try {
            const refreshed = await refreshTokens();
            if (refreshed?.access_token) {
              await saveToken(refreshed.access_token);
              await saveRefreshToken(refreshed.refresh_token);
            }
          } catch {
            // No valid cookie → treat as logged out.
          }
        }

        const auth = await checkAuthenticated();
        setIsAuthenticated(auth);

        if (auth) {
          setOrgSelectedState(await getOrgSelected());
          const decoded = await decodeToken();
          setJwtPayload(decoded);

          if (decoded?.user_id) {
            const allAccounts = await accountStore.getAll();
            const exists = allAccounts.find(
              (a) => a.userId === decoded.user_id,
            );
            const accessToken = await getToken();
            const refreshToken = await getRefreshToken();
            const resolvedRefreshToken = refreshToken ?? exists?.refreshToken;

            if (accessToken && resolvedRefreshToken) {
              const role = toRoleArray(decoded.roles)[0] ?? exists?.role;
              const organization = decoded.org_id ?? exists?.organization;

              await accountStore.upsert({
                userId: decoded.user_id,
                email: decoded.email,
                accessToken,
                refreshToken: resolvedRefreshToken,
                role,
                organization,
              });
              await accountStore.setActive(decoded.user_id);
              setStoredRole(role);
            } else if (exists?.role) {
              setStoredRole(exists.role);
            }
          }

          setAccounts(await accountStore.getAll());
        }
      } catch (e) {
        setIsAuthenticated(false);
      } finally {
        setInitialized(true);
      }
    };

    initAuth();

    const unsubscribe = authEvents.onLogout(async () => {
      await clearTokens();
      setIsAuthenticated(false);
      setJwtPayload(null);
      setStoredRole(undefined);
      setAccounts([]);
      hasRedirected.current = false;
      setOrgSelectedState(false);
      router.replace("/(auth)/login");
    });

    return unsubscribe;
  }, []);

  const { data: blocksUser, isLoading } = useBlocksUserByIdQuery(
    jwtPayload?.user_id ?? "",
    { enabled: isAuthenticated && !!jwtPayload?.user_id },
  );
  const user: BlocksUser | null = blocksUser ?? null;

  // Never surface the "default" bookkeeping org as the active org.
  const rawOrgId = jwtPayload?.org_id;
  const orgId = rawOrgId && rawOrgId !== DEFAULT_ORG_ID ? rawOrgId : undefined;

  const { data: userGroups = null } = useGetUserGroupIdsQuery(
    user?.itemId as string,
    { enabled: !!orgId },
  );
  const appRole = deriveRole(user, jwtPayload, orgId, storedRole);
  // Tolerate casing/separator variants ("super-admin", "Super_Admin", ...).
  const normalize = (role?: string | null) =>
    (typeof role === "string" ? role : "").toLowerCase().replace(/[\s_-]/g, "");

  const currentOrgRoles =
    user?.memberships?.find((membership) => membership.organizationId === orgId)
      ?.roles ?? [];
  const { data: organizationsData } = useOrganizationsQuery({
    enabled: isAuthenticated,
  });
  const currentOrgName = organizationsData?.find(
    (org) => org.itemId === orgId,
  )?.name;

  // A user can hold multiple roles — the JWT `roles` claim becomes an array and
  // memberships carry a roles[]. Expose the de-duped union so permission checks
  // honor every role (e.g. "super-admin" + "member"), not just the first one.
  const roles = [
    ...new Set(
      [...toRoleArray(jwtPayload?.roles), ...currentOrgRoles].filter(
        (role): role is string => typeof role === "string" && role.length > 0,
      ),
    ),
  ];
  const roleCandidates = roles.map(normalize);
  const isSuperAdmin = roleCandidates.includes("superadmin");
  const isAdmin = isSuperAdmin || roleCandidates.includes("admin");

  useEffect(() => {
    const currentOrgMembership = user?.memberships?.find(
      (membership) => membership.organizationId === orgId,
    );
    const membershipRole = getPrimaryRole(currentOrgMembership?.roles);
    if (membershipRole) {
      setStoredRole(membershipRole);
      if (jwtPayload?.user_id) {
        accountStore.getAll().then((all) => {
          const existing = all.find((a) => a.userId === jwtPayload.user_id);
          if (existing && existing.role !== membershipRole) {
            accountStore.upsert({ ...existing, role: membershipRole });
          }
        });
      }
    }
  }, [user, orgId]);

  useEffect(() => {
    if (!initialized || isLoading || !user || hasRedirected.current) return;

    hasRedirected.current = true;

    const id = setTimeout(() => {
      const nonDefaultOrgs = getRealMemberships(user);
      const hasName = Boolean(user.firstName?.trim() || user.lastName?.trim());

      if (nonDefaultOrgs.length === 0) {
        router.replace("/(auth)/org-select?pending=true");
      } else if (!orgSelected) {
        router.replace("/(auth)/org-select");
      } else if (!hasName) {
        router.replace(`/profile/${user.itemId}/edit?onboarding=true`);
      } else {
        router.replace("/feeds");
      }
    }, 0);

    return () => clearTimeout(id);
  }, [initialized, isLoading, user, orgSelected]);

  useEffect(() => {
    if (isAuthenticated) {
      wasAuthenticated.current = true;
      return;
    }
    if (wasAuthenticated.current) {
      wasAuthenticated.current = false;
      queryClient.clear();
    }
  }, [isAuthenticated, queryClient]);

  useNotificationConnection(orgId ? user : null);

  const switchAccount = async (userId: string) => {
    setIsSwitching(true);
    try {
      const all = await accountStore.getAll();
      const target = all.find((a) => a.userId === userId);
      if (!target) return;
      let tokens: { access_token: string; refresh_token: string };
      try {
        tokens = await refreshTokens(target.refreshToken);
      } catch (err: any) {
        const status = err?.response?.status;
        if (status === 400 || status === 401 || status === 403) {
          await accountStore.remove(userId);
          setAccounts(await accountStore.getAll());
        }
        throw err;
      }

      await saveToken(tokens.access_token);
      await saveRefreshToken(tokens.refresh_token);
      await accountStore.setActive(userId);

      queryClient.clear();

      const decoded = await decodeToken();
      // Persist the refreshed tokens back to the store so the next switch/refresh
      // starts from the latest pair, not the now-rotated one.
      await accountStore.upsert({
        ...target,
        accessToken: tokens.access_token,
        refreshToken: tokens.refresh_token,
        role: decoded?.roles ?? target.role,
        organization: decoded?.org_id ?? target.organization,
      });

      setJwtPayload(decoded ?? null);
      setStoredRole(decoded?.roles ?? target.role);
      setIsAuthenticated(true);
      setAccounts(await accountStore.getAll());
      await setOrgSelected(false);
      setOrgSelectedState(false);
      hasRedirected.current = false;
    } finally {
      setIsSwitching(false);
    }
  };

  const addAccount = () => {
    router.push("/(auth)/login?mode=add");
  };

  const logout = async () => {
    const activeId = jwtPayload?.user_id;
    if (activeId) await accountStore.remove(activeId);

    if (Platform.OS === "web") await webServerLogout();
    await clearTokens();
    setIsAuthenticated(false);
    setJwtPayload(null);
    setStoredRole(undefined);
    hasRedirected.current = false;
    setOrgSelectedState(false);
    setAccounts(await accountStore.getAll());
    router.replace("/(auth)/login");
  };

  const refreshAuth = async (skipRedirect: boolean = false) => {
    try {
      if (Platform.OS === "web") invalidateIdentity();
      const auth = await checkAuthenticated();
      if (!auth) {
        await clearTokens();
        setIsAuthenticated(false);
        return;
      }

      const decoded = await decodeToken();
      const allAccounts = await accountStore.getAll();
      const exists = allAccounts.find((a) => a.userId === decoded?.user_id);
      const accessToken = await getToken();
      const refreshToken = await getRefreshToken();
      const resolvedRefreshToken = refreshToken ?? exists?.refreshToken;

      if (decoded && accessToken && resolvedRefreshToken) {
        const role = toRoleArray(decoded.roles)[0] ?? exists?.role;
        const organization = decoded.org_id ?? exists?.organization;

        await accountStore.upsert({
          userId: decoded.user_id,
          email: decoded.email,
          accessToken,
          refreshToken: resolvedRefreshToken,
          role,
          organization,
        });
        await accountStore.setActive(decoded.user_id);
        setStoredRole(role);
      } else if (exists?.role) {
        setStoredRole(exists.role);
      }

      queryClient.clear();

      setOrgSelectedState(await getOrgSelected());

      if (!skipRedirect) {
        hasRedirected.current = false;
      }

      setJwtPayload(decoded ?? null);
      setAccounts(await accountStore.getAll());
      setIsAuthenticated(auth);
    } catch {
      await clearTokens();
      setIsAuthenticated(false);
    }
  };

  if (!initialized || (isAuthenticated && isLoading)) {
    return (
      <View className="flex-1 justify-center items-center bg-chalk">
        <ActivityIndicator size="large" />
      </View>
    );
  }

  const value: AuthContextType = {
    user,
    userGroups,
    orgId,
    roles,
    isAdmin,
    isAuthenticated,
    isLoading,
    isSwitching,
    refreshAuth,
    logout,
    appRole,
    isSuperAdmin,
    accounts,
    switchAccount,
    addAccount,
    currentOrgName,
  };

  return (
    <>
      <AuthContext.Provider value={value}>
        {children}
        {isAuthenticated && orgId && <LeaderboardSeasonGate orgId={orgId} />}
      </AuthContext.Provider>
      {isSwitching && (
        <View className="absolute inset-0 z-[9999] bg-white/85 items-center justify-center">
          <ActivityIndicator size="large" />
        </View>
      )}
    </>
  );
};

export const useAuth = () => useContext(AuthContext)!;
